import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, Link2, Loader, X } from 'lucide-react';
import { apogeeApiService } from '../../services/apogeeApi';
import { partnersApi } from '../../services/partnersApi';
import type { CurseForgeFileRow } from '../../types/apogee';
import type { ReportView } from '../../types/partners';
import { cn } from '../../utils/cn';
import { versionInDisplayName } from '../../utils/partnerVersions';
import type { PartnerNotice } from '../../utils/partnerSync';

interface PartnerCurseForgeLinkPickerProps {
  report: ReportView;
  /** The pack's Apogee entry, whose cached CurseForge files are listed. */
  apogeeId: number;
  onLinked: (report: ReportView) => void;
  onNotice: (notice: PartnerNotice) => void;
  onClose: () => void;
}

const RELEASE_TYPES: Record<number, string> = { 1: 'Release', 2: 'Beta', 3: 'Alpha' };

/**
 * Say which CurseForge file a report is, when the watch could not match the
 * version in the file's name by itself. Files that do name the version are
 * marked, and the newest of them is chosen to start with.
 */
export const PartnerCurseForgeLinkPicker = ({ report, apogeeId, onLinked, onNotice, onClose }: PartnerCurseForgeLinkPickerProps) => {
  const [files, setFiles] = useState<CurseForgeFileRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<number | null>(report.curseforge?.file_id ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apogeeApiService
      .getPackCurseForgeFilesByApogeeId(apogeeId, 20)
      .then((res) => {
        if (cancelled) return;
        const items = [...(res.items ?? [])].sort((a, b) => b.cf_file_id - a.cf_file_id);
        setFiles(items);
        setChosen((current) => current ?? items.find((f) => versionInDisplayName(f.display_name, report.version))?.cf_file_id ?? null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? `Apogee could not list the CurseForge files. ${e.message}` : 'Apogee could not list the CurseForge files.');
      });
    return () => {
      cancelled = true;
    };
  }, [apogeeId, report.version]);

  const link = async () => {
    const file = files?.find((f) => f.cf_file_id === chosen);
    if (!file) return;
    setSaving(true);
    try {
      const updated = await partnersApi.linkCurseForge(report.id, { cf_file_id: file.cf_file_id, display_name: file.display_name });
      onLinked(updated);
      onNotice({ tone: 'success', text: `Matched ${report.pack_name} ${report.version} to the CurseForge file ${file.display_name}.` });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The file could not be linked.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="subcard p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-white">Which CurseForge file is {report.version}?</p>
        <button type="button" className="btn-icon btn-icon-sm" onClick={onClose} title="Close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>
      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
      {!files && !error ? (
        <p className="text-xs text-glass-400 flex items-center gap-2">
          <Loader className="w-3.5 h-3.5 animate-spin" /> Loading the pack’s recent CurseForge files…
        </p>
      ) : files && files.length === 0 ? (
        <p className="text-xs text-glass-400">Apogee has not seen any CurseForge files for this pack yet.</p>
      ) : (
        files && (
          <ul className="max-h-60 overflow-y-auto rounded-lg border border-glass-200/15 divide-y divide-glass-200/10" role="radiogroup" aria-label="CurseForge files">
            {files.map((file) => {
              const names = versionInDisplayName(file.display_name, report.version);
              return (
                <li key={file.cf_file_id}>
                  <label className={cn('flex items-start gap-2.5 px-3 py-2 cursor-pointer', chosen === file.cf_file_id ? 'bg-accent-500/10' : 'hover:bg-glass-200/10')}>
                    <input
                      type="radio"
                      name={`cf-${report.id}`}
                      className="mt-1"
                      checked={chosen === file.cf_file_id}
                      onChange={() => setChosen(file.cf_file_id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-white break-all">{file.display_name}</span>
                      <span className="block text-[11px] text-glass-500 tabular-nums">
                        {file.release_type ? `${RELEASE_TYPES[file.release_type] ?? 'Release'} · ` : ''}
                        {file.file_date ? `uploaded ${formatDistanceToNow(new Date(file.file_date), { addSuffix: true })} · ` : ''}
                        file {file.cf_file_id}
                      </span>
                    </span>
                    {names && <span className="badge badge-success shrink-0">Names {report.version}</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        )
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="glass-button-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={() => void link()} disabled={!chosen || saving || !files?.some((f) => f.cf_file_id === chosen)}>
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Link this file
        </button>
      </div>
    </div>
  );
};
